import React, { useEffect, useState } from 'react';
import api from '../../api/api';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProducts } from '../../store/slices/productSlice';
import { fetchSemiFinished } from '../../store/slices/semiFinishedSlice';
import { fetchDishes } from '../../store/slices/dishSlice';
import { Dish, InventoryAdjustment, Product, SemiFinishedProduct } from '../../types';
import { escapeHtml, getInventoryPrintStyles, getWeightTypeLabel } from '../../utils/inventoryPrint';
import { getSelectableProducts } from '../../utils/selectableProducts';
import { openAndPrintHtml } from '../../utils/printDocument';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';

type WriteOffRow = {
  kind: 'product' | 'semi' | 'dish';
  productId: string;
  semiFinishedProductId: string;
  dishId: string;
  quantity: string;
  comment: string;
};

type WriteOffMetaItem = {
  productId?: number | null;
  semiFinishedProductId?: number | null;
  dishId?: number | null;
  quantity: number;
  comment: string;
  expandedProducts?: { productId: number; quantity: number }[];
};

type WriteOffMetaPayload = { version?: number; rows: WriteOffMetaItem[] };

const PRINT_STYLE_STORAGE_KEY = 'crm4chef.writeoff.print-style.v1';
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const loadPrintStyle = (): { fontSize: number; cellPadding: number } => {
  try {
    const raw = JSON.parse(localStorage.getItem(PRINT_STYLE_STORAGE_KEY) || '{}');
    return {
      fontSize: clamp(Number(raw?.fontSize) || 12, 10, 20),
      cellPadding: clamp(Number(raw?.cellPadding) || 6, 2, 16),
    };
  } catch {
    return { fontSize: 12, cellPadding: 6 };
  }
};
const savePrintStyle = (fontSize: number, cellPadding: number) =>
  localStorage.setItem(PRINT_STYLE_STORAGE_KEY, JSON.stringify({ fontSize, cellPadding }));

const normalizeMetaItem = (row: any): WriteOffMetaItem => ({
  productId: row?.ProductId ?? row?.productId ?? null,
  semiFinishedProductId: row?.SemiFinishedProductId ?? row?.semiFinishedProductId ?? null,
  dishId: row?.DishId ?? row?.dishId ?? null,
  quantity: Number(row?.Quantity ?? row?.quantity ?? 0),
  comment: String(row?.Comment ?? row?.comment ?? '').trim(),
  expandedProducts: (row?.ExpandedProducts ?? row?.expandedProducts ?? []).map((p: any) => ({
    productId: Number(p?.ProductId ?? p?.productId ?? 0),
    quantity: Number(p?.Quantity ?? p?.quantity ?? 0),
  })).filter((p: { productId: number; quantity: number }) => p.productId > 0 && p.quantity > 0),
});

const parseWriteOffReason = (reasonRaw: string): { baseReason: string; metaRows: WriteOffMetaItem[] } => {
  const raw = reasonRaw || '';
  const separatorIndex = raw.indexOf('|');
  if (separatorIndex < 0) return { baseReason: raw.trim(), metaRows: [] };

  const baseReason = raw.slice(0, separatorIndex).trim();
  const metaRaw = raw.slice(separatorIndex + 1).trim();
  try {
    const parsed = JSON.parse(metaRaw);
    const sourceRows: any[] = Array.isArray(parsed) ? parsed : (parsed?.rows ?? parsed?.Rows ?? []);
    const metaRows = sourceRows
      .map(normalizeMetaItem)
      .filter((row: WriteOffMetaItem) =>
        row.quantity > 0 && ((row.productId ?? 0) > 0 || (row.semiFinishedProductId ?? 0) > 0 || (row.dishId ?? 0) > 0));
    return { baseReason, metaRows };
  } catch {
    return { baseReason, metaRows: [] };
  }
};

const buildWriteOffReason = (rows: WriteOffMetaItem[]) => {
  const payload: WriteOffMetaPayload = {
    version: 2,
    rows: rows.map((row) => ({
      productId: row.productId ?? null,
      semiFinishedProductId: row.semiFinishedProductId ?? null,
      dishId: row.dishId ?? null,
      quantity: row.quantity,
      comment: row.comment,
      expandedProducts: row.expandedProducts ?? [],
    })),
  };
  return `Списання|${JSON.stringify(payload)}`;
};

const thStyle: React.CSSProperties = { padding: '8px', textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const tdStyle: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--crm-table-row-border)' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'primary' | 'accent';
const btn = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'primary' ? 'var(--crm-navbar-dropdown-bg)' : tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone === 'accent' ? 'accent' : `${tone}-bg`})`,
  color: tone === 'primary' ? 'var(--crm-primary-contrast)' : tone === 'muted' ? 'var(--crm-text-muted)' : tone === 'accent' ? 'var(--crm-accent-contrast)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'primary' || tone === 'muted' ? 'border' : tone === 'accent' ? 'accent' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  cursor: 'pointer',
  padding: '8px 12px',
  ...extra,
});

type PickerTab = 'dish' | 'semi' | 'product';
function groupByCategory<T extends { categoryName?: string | null }>(items: T[]): { cat: string; items: T[] }[] {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const cat = item.categoryName || 'Без категорії';
    if (!map.has(cat)) map.set(cat, []);
    map.get(cat)!.push(item);
  }
  return Array.from(map.entries()).map(([cat, groupedItems]) => ({ cat, items: groupedItems }));
}

const WriteOffPickerModal: React.FC<{
  dishes: Dish[];
  semiFinished: SemiFinishedProduct[];
  products: Product[];
  onAdd: (kind: WriteOffRow['kind'], id: string, qty?: string) => void;
  onClose: () => void;
}> = ({ dishes, semiFinished, products, onAdd, onClose }) => {
  const [tab, setTab] = useState<PickerTab>('dish');
  const [search, setSearch] = useState('');
  const q = search.toLowerCase();

  const filteredDishes = q ? dishes.filter((d) => d.name.toLowerCase().includes(q)) : dishes;
  const filteredSemi = q ? semiFinished.filter((s) => s.name.toLowerCase().includes(q)) : semiFinished;
  const filteredProducts = q ? products.filter((p) => p.name.toLowerCase().includes(q)) : products;

  const dishGroups = groupByCategory(filteredDishes);
  const semiGroups = groupByCategory(filteredSemi);
  const productGroups = groupByCategory(filteredProducts);

  const tabBtn = (t: PickerTab, label: string) => (
    <button
      type="button"
      onClick={() => { setTab(t); setSearch(''); }}
      className={`crm-picker-tab ${tab === t ? 'crm-picker-tab--active' : ''}`}
    >
      {label}
    </button>
  );

  const itemRow = (kind: PickerTab, id: number, name: string) => (
    <div
      key={id}
      onClick={() => onAdd(kind, String(id), '1')}
      className="crm-picker-item"
    >
      <span>{name}</span>
      <span className="crm-picker-item-accent">+</span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      className="crm-picker-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="crm-picker-modal crm-mobile-modal-card"
      >
        <div className="crm-picker-header crm-mobile-mobile-row">
          <strong style={{ fontSize: '15px' }}>Додати позицію</strong>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--crm-text-muted)', lineHeight: 1 }}>✕</button>
        </div>

        <div className="crm-picker-tabs crm-mobile-mobile-row">
          {tabBtn('dish', `🍽 Страви (${filteredDishes.length})`)}
          {tabBtn('semi', `🧪 Н/Ф (${filteredSemi.length})`)}
          {tabBtn('product', `📦 Продукти (${filteredProducts.length})`)}
        </div>

        <div className="crm-picker-search-wrap crm-mobile-mobile-row">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔍 Пошук..."
            style={{ width: '100%', padding: '7px 10px', border: '1px solid var(--crm-border)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }}
          />
        </div>

        <div className="crm-picker-list crm-mobile-modal-content">
          {tab === 'dish' && (
            dishGroups.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Нічого не знайдено</div>
              : dishGroups.map(({ cat, items: groupedItems }) => (
                <div key={cat}>
                  <div className="crm-picker-group-title">{cat}</div>
                  {groupedItems.map((d) => itemRow('dish', d.id, d.name))}
                </div>
              ))
          )}
          {tab === 'semi' && (
            semiGroups.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Нічого не знайдено</div>
              : semiGroups.map(({ cat, items: groupedItems }) => (
                <div key={cat}>
                  <div className="crm-picker-group-title">{cat}</div>
                  {groupedItems.map((s) => itemRow('semi', s.id, s.name))}
                </div>
              ))
          )}
          {tab === 'product' && (
            productGroups.length === 0
              ? <div style={{ padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Нічого не знайдено</div>
              : productGroups.map(({ cat, items: groupedItems }) => (
                <div key={cat}>
                  <div className="crm-picker-group-title">{cat}</div>
                  {groupedItems.map((p) => itemRow('product', p.id, p.name))}
                </div>
              ))
          )}
        </div>
        <div className="crm-picker-footer">
          <button onClick={onClose} style={btn('muted')}>Готово</button>
        </div>
      </div>
    </div>
  );
};

const InventoryWriteOffOpsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { products } = useAppSelector((s) => s.product);
  const { items: semiFinished } = useAppSelector((s) => s.semiFinished);
  const dishes = useAppSelector((s) => s.dish.items);

  const [items, setItems] = useState<InventoryAdjustment[]>([]);
  const [selected, setSelected] = useState<InventoryAdjustment | null>(null);
  const [selectedWriteOffIds, setSelectedWriteOffIds] = useState<number[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const initialPrintStyle = React.useMemo(() => loadPrintStyle(), []);
  const [printFontSize, setPrintFontSize] = useState(initialPrintStyle.fontSize);
  const [printCellPadding, setPrintCellPadding] = useState(initialPrintStyle.cellPadding);
  const [expandOnPrint, setExpandOnPrint] = useState(false);
  const [modalMetaRow, setModalMetaRow] = useState<WriteOffMetaItem | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [warehouseId, setWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<WriteOffRow[]>([]);

  const resetForm = () => {
    setDate(new Date().toISOString().slice(0, 10));
    setWarehouseId('');
    setNotes('');
    setRows([]);
    setEditingId(null);
    setModalMetaRow(null);
  };

  useEffect(() => {
    savePrintStyle(printFontSize, printCellPadding);
  }, [printFontSize, printCellPadding]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<InventoryAdjustment[]>('/inventoryadjustments', { params: { operationType: 'WriteOff' } });
      setItems(res.data);
      setSelectedWriteOffIds((prev) => prev.filter((id) =>
        res.data.some((row) => row.id === id && row.status !== 'Draft' && row.status !== 'Cancelled')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchSemiFinished());
    dispatch(fetchDishes());
    load();
  }, [dispatch]);

  const open = async (id: number) => {
    const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
    setSelected(res.data);
    setModalMetaRow(null);
  };

  const toUiRow = (row: WriteOffMetaItem): WriteOffRow => ({
    kind: row.dishId ? 'dish' : row.semiFinishedProductId ? 'semi' : 'product',
    productId: row.productId ? String(row.productId) : '',
    semiFinishedProductId: row.semiFinishedProductId ? String(row.semiFinishedProductId) : '',
    dishId: row.dishId ? String(row.dishId) : '',
    quantity: String(row.quantity),
    comment: row.comment || '',
  });

  const startEdit = (item: InventoryAdjustment) => {
    setEditingId(item.id);
    setDate(item.date.slice(0, 10));
    setWarehouseId(item.warehouseId ? String(item.warehouseId) : '');
    const parsedReason = parseWriteOffReason(item.reason || '');
    setNotes(item.notes || '');
    if (parsedReason.metaRows.length > 0) {
      setRows(parsedReason.metaRows.map(toUiRow));
    } else {
      const mapped = (item.items || [])
        .filter((r) => r.quantityDelta < 0)
        .map((r) => ({
          kind: 'product' as const,
          productId: r.productId ? String(r.productId) : '',
          semiFinishedProductId: '',
          dishId: '',
          quantity: String(Math.abs(r.quantityDelta)),
          comment: '',
        }));
      setRows(mapped.length ? mapped : []);
    }
    setShowForm(true);
  };

  const updateRow = (idx: number, patch: Partial<WriteOffRow>) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx: number) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const addFromPicker = (kind: WriteOffRow['kind'], id: string, qty = '1') => {
    setRows((prev) => [...prev, { kind, productId: kind === 'product' ? id : '', semiFinishedProductId: kind === 'semi' ? id : '', dishId: kind === 'dish' ? id : '', quantity: qty, comment: '' }]);
  };

  const productNameById = new Map(products.map((p) => [p.id, p.name]));
  const semiNameById = new Map(semiFinished.map((s) => [s.id, s.name]));
  const dishNameById = new Map(dishes.map((d) => [d.id, d.name]));
  const semiById = new Map(semiFinished.map((s) => [s.id, s]));
  const dishById = new Map(dishes.map((d) => [d.id, d]));

  const addTotal = (target: Map<number, number>, productId: number, quantity: number) => {
    if (productId <= 0 || quantity <= 0) return;
    target.set(productId, (target.get(productId) || 0) + quantity);
  };

  const expandSemiToProducts = (
    semiId: number,
    multiplier: number,
    totals: Map<number, number>,
    visited: Set<number>
  ) => {
    const semi = semiById.get(semiId);
    if (!semi || multiplier <= 0) return;
    if (visited.has(semiId)) return;
    visited.add(semiId);

    for (const item of semi.items) {
      const baseQty = item.bruttoWeight > 0 ? item.bruttoWeight : item.quantity;
      const effectiveQty = baseQty * multiplier;
      if (effectiveQty <= 0) continue;
      if (item.productId) {
        addTotal(totals, item.productId, effectiveQty);
      } else if (item.subSemiFinishedProductId) {
        expandSemiToProducts(item.subSemiFinishedProductId, effectiveQty, totals, visited);
      }
    }

    visited.delete(semiId);
  };

  const expandDishToProducts = (
    dishId: number,
    multiplier: number,
    totals: Map<number, number>,
    visitedDishes: Set<number>,
    visitedSemi: Set<number>
  ) => {
    const dish = dishById.get(dishId);
    if (!dish || multiplier <= 0) return;
    if (visitedDishes.has(dishId)) return;
    visitedDishes.add(dishId);

    for (const ingredient of dish.ingredients) {
      const baseQty = ingredient.bruttoWeight > 0 ? ingredient.bruttoWeight : ingredient.quantity;
      const effectiveQty = baseQty * multiplier;
      if (effectiveQty <= 0) continue;
      if (ingredient.productId) {
        addTotal(totals, ingredient.productId, effectiveQty);
      } else if (ingredient.semiFinishedProductId) {
        expandSemiToProducts(ingredient.semiFinishedProductId, effectiveQty, totals, visitedSemi);
      }
    }

    visitedDishes.delete(dishId);
  };

  const expandInputRowToProducts = (row: WriteOffRow): Map<number, number> => {
    const totals = new Map<number, number>();
    const quantity = Number(row.quantity);
    if (!(quantity > 0)) return totals;

    if (row.kind === 'product') {
      const productId = Number(row.productId);
      if (productId > 0) addTotal(totals, productId, quantity);
      return totals;
    }

    if (row.kind === 'semi') {
      const semiId = Number(row.semiFinishedProductId);
      if (semiId > 0) expandSemiToProducts(semiId, quantity, totals, new Set<number>());
      return totals;
    }

    const dishId = Number(row.dishId);
    if (dishId > 0) {
      expandDishToProducts(dishId, quantity, totals, new Set<number>(), new Set<number>());
    }
    return totals;
  };

  const save = async (e: React.SyntheticEvent, postImmediately = true) => {
    e.preventDefault();

    const normalizedRows = rows
      .map((r) => ({
        kind: r.kind,
        productId: r.kind === 'product' ? Number(r.productId) : null,
        semiFinishedProductId: r.kind === 'semi' ? Number(r.semiFinishedProductId) : null,
        dishId: r.kind === 'dish' ? Number(r.dishId) : null,
        quantity: Number(r.quantity),
        comment: (r.comment || '').trim(),
      }))
      .filter((r) => r.quantity > 0 && ((r.productId ?? 0) > 0 || (r.semiFinishedProductId ?? 0) > 0 || (r.dishId ?? 0) > 0));

    if (normalizedRows.length === 0) {
      alert('Додайте хоча б одну позицію з кількістю більше 0.');
      return;
    }

    if (normalizedRows.some((r) => !r.comment)) {
      alert('Заповніть коментар для кожної доданої позиції.');
      return;
    }

    const payloadTotals = new Map<number, number>();
    const metaRows: WriteOffMetaItem[] = [];

    for (const row of normalizedRows) {
      const uiRow: WriteOffRow = {
        kind: row.kind,
        productId: row.productId ? String(row.productId) : '',
        semiFinishedProductId: row.semiFinishedProductId ? String(row.semiFinishedProductId) : '',
        dishId: row.dishId ? String(row.dishId) : '',
        quantity: String(row.quantity),
        comment: row.comment,
      };
      const expanded = expandInputRowToProducts(uiRow);
      expanded.forEach((qty, productId) => addTotal(payloadTotals, productId, qty));

      metaRows.push({
        productId: row.productId ?? null,
        semiFinishedProductId: row.semiFinishedProductId ?? null,
        dishId: row.dishId ?? null,
        quantity: row.quantity,
        comment: row.comment,
        expandedProducts: Array.from(expanded.entries()).map(([productId, quantity]) => ({ productId, quantity })),
      });
    }

    const payloadItems = Array.from(payloadTotals.entries())
      .filter(([, quantity]) => quantity > 0)
      .map(([productId, quantity]) => ({ productId, quantity }));

    if (payloadItems.length === 0) {
      alert('Не вдалося сформувати списання до продуктів для обраних позицій.');
      return;
    }

    const payload = {
      date: new Date(date).toISOString(),
      warehouseId: warehouseId ? Number(warehouseId) : null,
      reason: buildWriteOffReason(metaRows),
      notes,
      items: payloadItems,
    };

    let savedId: number;
    if (editingId) {
      await api.put(`/inventoryadjustments/${editingId}/write-off`, payload);
      savedId = editingId;
      if (postImmediately) await api.post(`/inventoryadjustments/${editingId}/write-off/post`);
    } else {
      const response = await api.post<InventoryAdjustment>(
        postImmediately ? '/inventoryadjustments/write-off' : '/inventoryadjustments/write-off/draft',
        payload
      );
      savedId = response.data.id;
    }
    setShowForm(false);
    resetForm();
    await load();
    await open(savedId);
  };

  const saveDraftFromButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.currentTarget.form && !e.currentTarget.form.reportValidity()) return;
    void save(e, false);
  };

  const saveAndPostDraftFromButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.currentTarget.form && !e.currentTarget.form.reportValidity()) return;
    void save(e, true);
  };

  const postSelectedDraft = async (id: number) => {
    await api.post(`/inventoryadjustments/${id}/write-off/post`);
    await load();
    await open(id);
  };

  const remove = async (id: number) => {
    if (!window.confirm('Скасувати списання? Воно залишиться в історії, але більше не впливатиме на склад.')) return;
    const reason = window.prompt('Вкажіть причину скасування:');
    if (!reason?.trim()) return;
    await api.post(`/inventoryadjustments/${id}/cancel`, { reason: reason.trim() });
    if (selected?.id === id) setSelected(null);
    setSelectedWriteOffIds((prev) => prev.filter((x) => x !== id));
    await load();
  };

  const toggleWriteOffSelection = (id: number) => {
    setSelectedWriteOffIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAllVisibleWriteOffs = () => {
    const visibleIds = items.filter((x) => x.status !== 'Draft' && x.status !== 'Cancelled').map((x) => x.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedWriteOffIds.includes(id));
    if (allSelected) {
      setSelectedWriteOffIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedWriteOffIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const markSelectedWriteOffsAsAccounting = async () => {
    if (selectedWriteOffIds.length === 0) {
      alert('Оберіть хоча б одне списання.');
      return;
    }
    await api.put('/inventoryadjustments/accounting-transfer', {
      ids: selectedWriteOffIds,
      isTransferredToAccounting: true,
    });
    await load();
    if (selected && selectedWriteOffIds.includes(selected.id)) await open(selected.id);
  };

  const getMetaTypeName = (row: WriteOffMetaItem) => {
    if (row.dishId) return 'Страва';
    if (row.semiFinishedProductId) return 'Н/Ф';
    return 'Продукт';
  };
  const getMetaItemName = (row: WriteOffMetaItem) => {
    if (row.dishId) return dishNameById.get(row.dishId) || `Страва #${row.dishId}`;
    if (row.semiFinishedProductId) return semiNameById.get(row.semiFinishedProductId) || `Н/Ф #${row.semiFinishedProductId}`;
    if (row.productId) return productNameById.get(row.productId) || `Продукт #${row.productId}`;
    return '—';
  };
  const getRowDisplayName = (r: WriteOffRow) => {
    if (r.kind === 'dish' && r.dishId) return dishNameById.get(Number(r.dishId)) || `Страва #${r.dishId}`;
    if (r.kind === 'semi' && r.semiFinishedProductId) return semiNameById.get(Number(r.semiFinishedProductId)) || `Н/Ф #${r.semiFinishedProductId}`;
    if (r.kind === 'product' && r.productId) return productNameById.get(Number(r.productId)) || `Продукт #${r.productId}`;
    return '—';
  };
  const kindBadge = (kind: WriteOffRow['kind']) => {
    const map: Record<WriteOffRow['kind'], [string, string]> = {
      dish: ['info', 'Страва'],
      semi: ['warning', 'Н/Ф'],
      product: ['success', 'Продукт'],
    };
    const [tone, label] = map[kind];
    return <span style={{ display: 'inline-block', background: `var(--crm-${tone}-bg)`, color: `var(--crm-${tone})`, border: `1px solid var(--crm-${tone})`, borderRadius: '4px', padding: '1px 6px', fontSize: '11px', fontWeight: 700, marginRight: '4px', whiteSpace: 'nowrap' }}>{label}</span>;
  };

  const buildExpandedPrintRows = (metaRows: WriteOffMetaItem[]): { productId: number; name: string; quantity: number; comments: string[] }[] => {
    const totals = new Map<number, { quantity: number; comments: Set<string> }>();
    for (const row of metaRows) {
      const comment = (row.comment || '').trim();
      if (row.expandedProducts && row.expandedProducts.length > 0) {
        for (const ep of row.expandedProducts) {
          const current = totals.get(ep.productId) || { quantity: 0, comments: new Set<string>() };
          current.quantity += ep.quantity;
          if (comment) current.comments.add(comment);
          totals.set(ep.productId, current);
        }
      } else if (row.productId) {
        const current = totals.get(row.productId) || { quantity: 0, comments: new Set<string>() };
        current.quantity += row.quantity;
        if (comment) current.comments.add(comment);
        totals.set(row.productId, current);
      }
    }
    return Array.from(totals.entries()).map(([productId, value]) => ({
      productId,
      name: productNameById.get(productId) || `Продукт #${productId}`,
      quantity: value.quantity,
      comments: Array.from(value.comments),
    })).sort((a, b) => a.name.localeCompare(b.name, 'uk'));
  };

  const print = () => {
    if (!selected) return;
    const metaRows = parseWriteOffReason(selected.reason || '').metaRows;

    let details: string;
    let headerHtml: string;

    if (expandOnPrint && metaRows.length > 0) {
      const expanded = buildExpandedPrintRows(metaRows);
      details = expanded.map((ep) => `<tr>
        <td class="col-position">${escapeHtml(ep.name)}</td>
        <td class="col-unit" style="text-align:center;">${escapeHtml(getWeightTypeLabel(products.find((p) => p.id === ep.productId)?.weightType || ''))}</td>
        <td class="col-qty" style="text-align:right;">${ep.quantity.toFixed(3)}</td>
        <td class="col-comment">${escapeHtml(ep.comments.join('; ') || '—')}</td>
      </tr>`).join('');
      headerHtml = '<div style="background:#fff3cd;border:1px solid #ffc107;border-radius:6px;padding:6px 10px;margin-top:6px;font-size:12px;"><strong>⚠ Розгорнуто до продуктів</strong> (страви та Н/Ф розкладені до складників)</div>';
    } else if (metaRows.length > 0) {
      details = metaRows.map((row) => {
        const unit = row.dishId
          ? 'порц'
          : row.semiFinishedProductId
            ? getWeightTypeLabel(semiFinished.find((s) => s.id === row.semiFinishedProductId)?.weightType || '')
            : getWeightTypeLabel(row.productId ? products.find((p) => p.id === row.productId)?.weightType : '');
        return `<tr>
          <td class="col-position">${escapeHtml(getMetaItemName(row))}</td>
          <td class="col-unit" style="text-align:center;">${escapeHtml(unit || '—')}</td>
          <td class="col-qty" style="text-align:right;">${row.quantity.toFixed(3)}</td>
          <td class="col-comment">${escapeHtml(row.comment || '—')}</td>
        </tr>`;
      }).join('');
      headerHtml = '';
    } else {
      details = (selected.items || []).filter((i) => i.quantityDelta < 0).map((i) => `<tr>
        <td class="col-position">${escapeHtml(i.productName || '—')}</td>
        <td class="col-unit" style="text-align:center;">${escapeHtml(getWeightTypeLabel(i.weightType))}</td>
        <td class="col-qty" style="text-align:right;">${Math.abs(i.quantityDelta).toFixed(3)}</td>
        <td class="col-comment">—</td>
      </tr>`).join('');
      headerHtml = '';
    }

    void openAndPrintHtml(`
      <html><head><title>Списання #${selected.id}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;color:#2c3e50}${getInventoryPrintStyles()} .pdf-receipt-root th, .pdf-receipt-root td { font-size:${printFontSize}px !important; padding:${printCellPadding}px ${printCellPadding + 2}px !important; } .pdf-receipt-root table{table-layout:fixed;} .pdf-receipt-root td{word-break:break-word;overflow-wrap:anywhere;} .pdf-receipt-root .col-position{width:40%;} .pdf-receipt-root .col-unit{width:5ch;white-space:nowrap;} .pdf-receipt-root .col-qty{width:7ch;white-space:nowrap;} .pdf-receipt-root .col-comment{width:40%;}</style>
      </head><body>
      <div class="pdf-receipt-root">
        <section class="receipt-sheet">
          <div class="receipt-top">
            <div class="receipt-title-row">
              <h2>Списання #${selected.id}</h2>
              <span class="page-index">Сторінка 1 / 1</span>
            </div>
            <div class="receipt-meta-grid">
              <div><strong>Компанія:</strong> Весільний Зал</div>
              <div><strong>Дата:</strong> ${new Date(selected.date).toLocaleDateString('uk-UA')}</div>
              <div><strong>Операція:</strong> Списання</div>
              <div><strong>Примітка:</strong> ${escapeHtml(selected.notes || '—')}</div>
            </div>
            ${headerHtml}
          </div>
          <table>
            <thead><tr><th class="col-position">Позиція</th><th class="col-unit" style="text-align:center;">Од.</th><th class="col-qty" style="text-align:right;">К-сть</th><th class="col-comment">Коментар</th></tr></thead>
            <tbody>${details || '<tr><td colspan="4" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>'}</tbody>
          </table>
          <div class="receipt-bottom">
            <div>Списав(ла): ____________________</div>
            <div>Прийняв(ла): ____________________</div>
          </div>
        </section>
      </div>
      </body></html>`, { windowFeatures: 'width=1000,height=700' });
  };

  const printSelectedWriteOffsSheet = async () => {
    if (selectedWriteOffIds.length === 0) {
      alert('Оберіть хоча б одне списання для друку.');
      return;
    }
    const docsRaw = await Promise.all(
      selectedWriteOffIds.map(async (id) => {
        try {
          const res = await api.get<InventoryAdjustment>(`/inventoryadjustments/${id}`);
          return res.data;
        } catch {
          return null;
        }
      })
    );
    const docs = docsRaw.filter((x): x is InventoryAdjustment => x != null);
    if (docs.length === 0) {
      alert('Не вдалося завантажити обрані списання для друку.');
      return;
    }

    const sortedDocs = [...docs].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const periodFrom = new Date(sortedDocs[0].date).toLocaleDateString('uk-UA');
    const periodTo = new Date(sortedDocs[sortedDocs.length - 1].date).toLocaleDateString('uk-UA');

    const rows = sortedDocs.flatMap((doc) => {
      const parsed = parseWriteOffReason(doc.reason || '');
      const reason = parsed.baseReason || 'Списання';
      const metaRows = parsed.metaRows;

      if (metaRows.length === 0) {
        return (doc.items || []).filter((i) => i.quantityDelta < 0).map((i) => ({
          date: new Date(doc.date).toLocaleDateString('uk-UA'),
          reason,
          productName: i.productName || '—',
          unit: getWeightTypeLabel(i.weightType),
          quantity: Math.abs(i.quantityDelta),
          comment: '',
        }));
      }

      if (expandOnPrint) {
        return buildExpandedPrintRows(metaRows).map((row) => ({
          date: new Date(doc.date).toLocaleDateString('uk-UA'),
          reason,
          productName: row.name,
          unit: getWeightTypeLabel(products.find((p) => p.id === row.productId)?.weightType || ''),
          quantity: row.quantity,
          comment: row.comments.join('; '),
        }));
      }

      return metaRows.map((row) => ({
        date: new Date(doc.date).toLocaleDateString('uk-UA'),
        reason,
        productName: getMetaItemName(row),
        unit: row.dishId
          ? 'порц'
          : row.semiFinishedProductId
            ? getWeightTypeLabel(semiFinished.find((s) => s.id === row.semiFinishedProductId)?.weightType || '')
            : getWeightTypeLabel(row.productId ? products.find((p) => p.id === row.productId)?.weightType : ''),
        quantity: row.quantity,
        comment: row.comment || '',
      }));
    });

    const rowsHtml = rows.map((row) => `
      <tr>
        <td>${escapeHtml(row.date)}</td>
        <td>${escapeHtml(row.productName)}</td>
        <td style="text-align:center;">${escapeHtml(row.unit)}</td>
        <td style="text-align:right;">${row.quantity.toFixed(3)}</td>
        <td>${escapeHtml(row.comment)}</td>
      </tr>`).join('');

    void openAndPrintHtml(`<html><head><title>Списання за період</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body{font-family:Arial,sans-serif;padding:14px;color:#2c3e50}
        ${getInventoryPrintStyles()}
        .pdf-receipt-root table { table-layout: fixed; width: 100%; }
        .pdf-receipt-root th, .pdf-receipt-root td { font-size:${printFontSize}px !important; padding:${printCellPadding}px ${printCellPadding + 2}px !important; vertical-align: top; }
        .pdf-receipt-root td { word-break: break-word; overflow-wrap: anywhere; }
        .pdf-receipt-root .col-date { width: 14%; white-space: nowrap; }
        .pdf-receipt-root .col-product { width: 49%; }
        .pdf-receipt-root .col-unit { width: 9%; text-align:center; white-space: nowrap; }
        .pdf-receipt-root .col-qty { width: 10%; text-align:right; white-space: nowrap; }
        .pdf-receipt-root .col-comment { width: 18%; }
      </style>
      </head><body>
      <div class="pdf-receipt-root"><section class="receipt-sheet">
        <div class="receipt-top">
          <div class="receipt-title-row">
            <h2>Списання за період з ${periodFrom} по ${periodTo}</h2>
            <span class="page-index">Обрано документів: ${sortedDocs.length}</span>
          </div>
          ${expandOnPrint ? '<div style="background:#fff3cd;border:1px solid #ffc107;border-radius:6px;padding:6px 10px;margin-top:6px;font-size:12px;"><strong>⚠ Розгорнуто до продуктів</strong> (страви та Н/Ф розкладені до складників)</div>' : ''}
        </div>
        <table>
          <thead><tr>
            <th class="col-date">Дата списання</th>
            <th class="col-product">Назва позиції</th>
            <th class="col-unit">Тип ваги</th>
            <th class="col-qty">Вага</th>
            <th class="col-comment">Коментар</th>
          </tr></thead>
          <tbody>${rowsHtml || '<tr><td colspan="5" style="text-align:center;color:#7f8c8d;">Позиції відсутні</td></tr>'}</tbody>
        </table>
      </section></div>
      </body></html>`, { windowFeatures: 'width=1200,height=800' });
  };

  const selectedParsedReason = selected ? parseWriteOffReason(selected.reason || '') : { baseReason: '', metaRows: [] };
  const selectedMetaRows = selectedParsedReason.metaRows;
  const hasExpandable = selectedMetaRows.some((r) => (r.dishId || r.semiFinishedProductId) && (r.expandedProducts?.length || 0) > 0);

  const getListPrimaryPosition = (adjustment: InventoryAdjustment) => {
    const parsed = parseWriteOffReason(adjustment.reason || '');
    if (parsed.metaRows.length === 0) return parsed.baseReason || adjustment.reason || '—';
    const first = getMetaItemName(parsed.metaRows[0]);
    return parsed.metaRows.length > 1 ? `${first} +${parsed.metaRows.length - 1}` : first;
  };

  return (
    <div className="page-shell page-shell--wide" style={{ maxWidth: '1500px' }}>
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-danger)', paddingBottom: '8px' }}>Списання</h2>
      <div className="no-print responsive-split-layout" style={{ marginTop: '16px' }}>
        <div style={{ flex: '0 0 420px' }}>
          <div style={{ marginBottom: '12px' }}>
            <button onClick={() => { resetForm(); setShowForm(true); }} style={btn('success', { width: '100%' })}>+ Нове списання</button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedWriteOffIds.length === 0}
              onClick={printSelectedWriteOffsSheet}
              style={btn(selectedWriteOffIds.length === 0 ? 'muted' : 'primary', { width: '100%' })}
            >
              🖨 Друк обраних ({selectedWriteOffIds.length})
            </button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <button
              type="button"
              disabled={selectedWriteOffIds.length === 0}
              onClick={markSelectedWriteOffsAsAccounting}
              style={btn(selectedWriteOffIds.length === 0 ? 'muted' : 'success', { width: '100%' })}
            >
              ✓ Передані в бухгалтерію ({selectedWriteOffIds.length})
            </button>
          </div>
          <div style={{ marginBottom: '12px', background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '10px' }}>
            <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--crm-text)', marginBottom: '8px' }}>Налаштування друку</div>
            <div className="inventory-print-settings-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
              <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
                Шрифт (px)
                <input type="number" min="10" max="20" step="1" value={printFontSize} onChange={(e) => setPrintFontSize(clamp(Number(e.target.value) || 12, 10, 20))} style={{ width: '100%', marginTop: '4px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
              </label>
              <label style={{ fontSize: '12px', color: 'var(--crm-text)' }}>
                Відступ (px)
                <input type="number" min="2" max="16" step="1" value={printCellPadding} onChange={(e) => setPrintCellPadding(clamp(Number(e.target.value) || 6, 2, 16))} style={{ width: '100%', marginTop: '4px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' }} />
              </label>
              <button type="button" onClick={() => { setPrintFontSize(12); setPrintCellPadding(6); }} style={btn('muted', { padding: '6px 10px', fontSize: '12px' })}>Скинути</button>
            </div>
          </div>
          <div className="crm-mobile-table-scroll" style={{ maxHeight: '620px', overflowY: 'auto', background: 'var(--crm-surface)', boxShadow: 'var(--crm-shadow-sm)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={{ ...thStyle, width: '32px', textAlign: 'center' }}><input type="checkbox" checked={items.some((x) => x.status !== 'Draft' && x.status !== 'Cancelled') && items.filter((x) => x.status !== 'Draft' && x.status !== 'Cancelled').every((x) => selectedWriteOffIds.includes(x.id))} onChange={toggleAllVisibleWriteOffs} title="Обрати проведені" /></th><th style={thStyle}>Дата</th><th style={thStyle}>Позиція</th><th style={thStyle}>Примітка</th><th style={thStyle}>Бух.</th><th style={thStyle}>Σ списання</th><th style={thStyle}>Статус</th></tr></thead>
              <tbody>
                {items.map((i) => (
                  <tr key={i.id} style={{ cursor: 'pointer', background: selected?.id === i.id ? 'var(--crm-danger-bg)' : undefined }} onClick={() => open(i.id)}>
                    <td style={{ ...tdStyle, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}><input type="checkbox" disabled={i.status === 'Draft' || i.status === 'Cancelled'} checked={selectedWriteOffIds.includes(i.id)} onChange={() => toggleWriteOffSelection(i.id)} title={`Обрати списання #${i.id}`} /></td>
                    <td style={tdStyle}>{new Date(i.date).toLocaleDateString('uk-UA')}</td>
                    <td style={tdStyle}>{getListPrimaryPosition(i)}</td>
                    <td style={tdStyle}>{i.notes || '—'}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: i.isTransferredToAccounting ? 'var(--crm-success)' : 'var(--crm-text-muted)', fontWeight: 700 }}>{i.isTransferredToAccounting ? '✓' : '—'}</td>
                    <td style={tdStyle}>{Math.abs(i.totalDelta).toFixed(3)}</td>
                    <td style={{ ...tdStyle, color: i.status === 'Draft' ? 'var(--crm-warning)' : i.status === 'Cancelled' ? 'var(--crm-text-muted)' : 'var(--crm-success)', fontWeight: 700 }}>
                      {i.status === 'Draft' ? 'Чернетка' : i.status === 'Cancelled' ? 'Скасовано' : 'Проведено'}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={7} style={{ padding: '16px', textAlign: 'center' }}>{loading ? 'Завантаження...' : 'Даних немає'}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          {showForm && (
            <form onSubmit={(e) => void save(e, !editingId)} className="inventory-receipts-card" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)', marginBottom: '16px' }}>
              <h3>{editingId ? 'Редагування чернетки списання' : 'Нове списання'}</h3>
              <div className="inventory-receipts-form-grid" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <label>Дата:<input type="date" required value={date} onChange={(e) => setDate(e.target.value)} /></label>
                <InventoryWarehouseSelect value={warehouseId} onChange={setWarehouseId} />
                <label style={{ flex: 1 }}>Примітка:<input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
              </div>

              {rows.length > 0 && (
                <table className="inventory-receipts-items-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: '55%' }}>Позиція</th>
                      <th style={{ ...thStyle, width: '15%' }}>К-сть</th>
                      <th style={{ ...thStyle, width: '25%' }}>Коментар</th>
                      <th style={{ ...thStyle, width: '5%' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, idx) => (
                      <tr key={idx}>
                        <td style={tdStyle}>
                          {kindBadge(r.kind)}
                          <span style={{ fontSize: '13px' }}>{getRowDisplayName(r)}</span>
                        </td>
                        <td style={tdStyle}>
                          <input
                            type="number" step="0.001" min="0.001" required
                            value={r.quantity}
                            onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                            style={{ width: '100%', padding: '4px 6px' }}
                          />
                        </td>
                        <td style={tdStyle}>
                          <input required value={r.comment} onChange={(e) => updateRow(idx, { comment: e.target.value })} placeholder="Коментар до позиції..." style={{ width: '100%', padding: '4px 6px' }} />
                        </td>
                        <td style={tdStyle}>
                          <button type="button" onClick={() => removeRow(idx)} style={btn('danger', { padding: '4px 8px' })}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => setPickerOpen(true)} style={btn('danger', { borderRadius: '999px' })}>
                  + Додати позицію
                </button>
                {rows.length === 0 && (
                  <span style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>Натисніть "+ Додати позицію" щоб обрати страву, Н/Ф або продукт</span>
                )}
              </div>

              <div className="inventory-receipts-form-actions" style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" style={btn('success', { borderRadius: '999px' })}>{editingId ? 'Зберегти чернетку' : 'Зберегти та провести'}</button>
                {editingId
                  ? <button type="button" onClick={saveAndPostDraftFromButton} style={btn('success', { borderRadius: '999px' })}>Зберегти та провести</button>
                  : <button type="button" onClick={saveDraftFromButton} style={btn('warning', { borderRadius: '999px' })}>Зберегти чернетку</button>}
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={btn('muted', { borderRadius: '999px' })}>Скасувати</button>
              </div>
            </form>
          )}

          {selected && !showForm && (
            <div className="print-area" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', padding: '16px', boxShadow: 'var(--crm-shadow-sm)' }}>
              <div className="inventory-receipts-detail-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <h3>Списання #{selected.id} {selected.status === 'Cancelled' ? '· СКАСОВАНО' : selected.status === 'Draft' ? '· ЧЕРНЕТКА' : '· ПРОВЕДЕНО'}</h3>
                <div className="no-print inventory-receipts-detail-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {hasExpandable && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: 'var(--crm-danger)', fontWeight: 600 }}>
                      <input type="checkbox" checked={expandOnPrint} onChange={(e) => setExpandOnPrint(e.target.checked)} style={{ cursor: 'pointer' }} />
                      Розгорнути до продуктів при друці
                    </label>
                  )}
                  {selected.status === 'Draft' && <button onClick={() => startEdit(selected)} style={btn('warning')}>Редагувати</button>}
                  {selected.status === 'Draft' && <button onClick={() => postSelectedDraft(selected.id)} style={btn('success')}>Провести списання</button>}
                  {selected.status !== 'Cancelled' && <button onClick={() => remove(selected.id)} style={btn('danger')}>Скасувати</button>}
                  <button onClick={print} style={btn('muted')}>Друк</button>
                </div>
              </div>
              {selected.status !== 'Draft' && selected.status !== 'Cancelled' && <p style={{ color: 'var(--crm-text-muted)' }}>Проведений документ незмінний. Для виправлення скасуйте його та створіть новий.</p>}
              {selected.postedAtUtc && <p>Проведено: {new Date(selected.postedAtUtc).toLocaleString('uk-UA')} · Автор: {selected.postedByUsername || 'історичний запис'}</p>}
              {selected.status === 'Draft' && selected.createdAt && <p style={{ color: 'var(--crm-warning)', fontWeight: 600 }}>Чернетка створена: {new Date(selected.createdAt).toLocaleString('uk-UA')} · не впливає на залишок</p>}
              <p>Дата: {new Date(selected.date).toLocaleDateString('uk-UA')}</p>
              {selected.notes && <p>Примітка: {selected.notes}</p>}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Тип</th><th style={thStyle}>Позиція</th><th style={{ ...thStyle, textAlign: 'center' }}>Од.</th><th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th><th style={thStyle}>Коментар</th></tr></thead>
                <tbody>
                  {selectedMetaRows.length > 0 ? selectedMetaRows.map((row, idx) => {
                    const expandable = !!(row.dishId || row.semiFinishedProductId) && (row.expandedProducts?.length || 0) > 0;
                    const unitLabel = row.dishId ? 'порц'
                      : row.semiFinishedProductId ? getWeightTypeLabel(semiFinished.find((s) => s.id === row.semiFinishedProductId)?.weightType || '')
                      : getWeightTypeLabel(row.productId ? products.find((p) => p.id === row.productId)?.weightType : '');
                    return (
                      <tr key={`meta-${idx}`} onClick={() => { if (expandable) setModalMetaRow(row); }} title={expandable ? 'Натисніть для перегляду складу' : undefined} style={{ cursor: expandable ? 'pointer' : 'default' }}>
                        <td style={tdStyle}>{getMetaTypeName(row)}</td>
                        <td style={{ ...tdStyle, color: expandable ? 'var(--crm-danger)' : undefined }}>{getMetaItemName(row)}{expandable ? ' 🔍' : ''}</td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>{unitLabel || '—'}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{row.quantity.toFixed(3)}</td>
                        <td style={tdStyle}>{row.comment || '—'}</td>
                      </tr>
                    );
                  }) : (selected.items || []).filter((i) => i.quantityDelta < 0).map((i) => (
                    <tr key={i.id}><td style={tdStyle}>Продукт</td><td style={tdStyle}>{i.productName || '—'}</td><td style={{ ...tdStyle, textAlign: 'center' }}>{getWeightTypeLabel(i.weightType)}</td><td style={{ ...tdStyle, textAlign: 'right' }}>{Math.abs(i.quantityDelta).toFixed(3)}</td><td style={tdStyle}>—</td></tr>
                  ))}
                </tbody>
              </table>
              {(selected.items || []).some((item) => item.quantityDelta < 0) && (
                <div style={{ marginTop: 12, padding: 10, background: 'var(--crm-info-bg)', border: '1px solid var(--crm-border)', borderRadius: 8, fontSize: 12 }}>
                  <strong>Простежуваність партій</strong>
                  {(selected.items || []).filter((item) => item.quantityDelta < 0).map((item) => (
                    <div key={`trace-${item.id}`} style={{ marginTop: 7 }}>
                      <span>{item.productName || 'Продукт'}: простежено {item.allocatedQuantity.toFixed(3)} з {Math.abs(item.quantityDelta).toFixed(3)}</span>
                      {item.unallocatedQuantity > 0 && <span style={{ color: 'var(--crm-warning)' }}> · без партії {item.unallocatedQuantity.toFixed(3)}</span>}
                      {item.allocations.map((allocation) => (
                        <div key={allocation.id} style={{ color: 'var(--crm-text-muted)', marginLeft: 12 }}>
                          Партія {allocation.lotNumber || 'без номера'} · {allocation.quantity.toFixed(3)} · {allocation.unitCost.toFixed(2)} грн
                          {allocation.expirationDate ? ` · до ${new Date(allocation.expirationDate).toLocaleDateString('uk-UA')}` : ''}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {modalMetaRow && (
        <div onClick={() => setModalMetaRow(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: '20px' }}>
          <div className="write-off-meta-dialog" onClick={(e) => e.stopPropagation()} style={{ width: 'min(760px, 96vw)', maxHeight: '85vh', overflow: 'auto', background: 'var(--crm-surface)', color: 'var(--crm-text)', borderRadius: '12px', boxShadow: 'var(--crm-shadow-sm)', padding: '16px' }}>
            <div className="write-off-meta-dialog__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>{getMetaTypeName(modalMetaRow)}: {getMetaItemName(modalMetaRow)}</h3>
              <button onClick={() => setModalMetaRow(null)} style={btn('muted')}>Закрити</button>
            </div>
            <div className="write-off-meta-dialog__table">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={thStyle}>Списаний продукт</th><th style={{ ...thStyle, textAlign: 'center' }}>Од.</th><th style={{ ...thStyle, textAlign: 'right' }}>К-сть</th></tr></thead>
                <tbody>
                  {(modalMetaRow.expandedProducts || []).map((p) => (
                    <tr key={`modal-${p.productId}`}>
                      <td style={tdStyle}>{productNameById.get(p.productId) || `Продукт #${p.productId}`}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{getWeightTypeLabel(products.find((x) => x.id === p.productId)?.weightType || '')}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{p.quantity.toFixed(3)}</td>
                    </tr>
                  ))}
                  {(modalMetaRow.expandedProducts || []).length === 0 && (
                    <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center' }}>Немає деталізації</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {pickerOpen && (
        <WriteOffPickerModal
          dishes={dishes}
          semiFinished={semiFinished}
          products={getSelectableProducts(products, rows.filter(r => r.kind === 'product').map(r => Number(r.productId)))}
          onAdd={(kind, id, qty) => addFromPicker(kind, id, qty)}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
};

export default InventoryWriteOffOpsPage;
